
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, X } from 'lucide-react';

export interface CommentFormProps {
  onSubmit: (content: string) => void;
  isSubmitting: boolean;
  isAuthenticated: boolean;
  placeholder?: string;
  buttonText?: string;
  compact?: boolean;
  autoFocus?: boolean;
  onCancel?: () => void;
}

const CommentForm: React.FC<CommentFormProps> = ({ 
  onSubmit, 
  isSubmitting,
  isAuthenticated,
  placeholder = "Write a comment...",
  buttonText = "Comment",
  compact = false,
  autoFocus = false,
  onCancel
}) => {
  const [content, setContent] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim()) return;
    
    onSubmit(content);
    setContent('');
  };

  return (
    <form onSubmit={handleSubmit} className={`mt-2 ${compact ? 'space-y-2' : 'space-y-4'}`}>
      {!isAuthenticated ? (
        <div className="text-sm text-center p-2 bg-muted/50 rounded">
          Please sign in to leave a comment
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={placeholder}
              disabled={isSubmitting}
              className={compact ? "text-sm h-8" : ""}
            />
            <Button
              type="submit"
              disabled={!content.trim() || isSubmitting}
              size={compact ? "sm" : "default"}
            >
              {isSubmitting ? (
                <div className="animate-spin h-4 w-4 border-2 border-t-transparent rounded-full"></div>
              ) : compact ? (
                <Send className="h-4 w-4" />
              ) : (
                buttonText
              )}
            </Button>
            
            {onCancel && (
              <Button 
                type="button" 
                variant="ghost" 
                size={compact ? "sm" : "default"} 
                onClick={onCancel}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </>
      )}
    </form>
  );
};

export default CommentForm;
